import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Lock,
  Crown,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Info,
  FileText,
  ArrowRight,
  ArrowLeft,
  Users,
  UserPlus,
  Settings2,
  Eye,
  Check,
  X,
  ChevronRight,
  FileUp,
  Sparkles,
  Trash2,
  History,
  Undo2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Piani con accesso
const PIANI_ABILITATI = ['governance', 'pro', 'enterprise', 'convenzioni'];

export default function ImportMassivoIbrido() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  // Stati generali
  const [loading, setLoading] = useState(true);
  const [importInfo, setImportInfo] = useState(null);
  
  // Step wizard
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Step 2: Mappatura
  const [mappatura, setMappatura] = useState({});
  const [tipoImport, setTipoImport] = useState('soci');
  
  // Step 3: Preview
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [gestioneDuplicati, setGestioneDuplicati] = useState('salta');
  
  // Step 4: Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Rollback
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [cronologiaImport, setCronologiaImport] = useState([]);
  const [loadingCronologia, setLoadingCronologia] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [selectedRollbackDate, setSelectedRollbackDate] = useState(null);
  
  // Aggiungi Volontari
  const [showAggiungiVolontariDialog, setShowAggiungiVolontariDialog] = useState(false);
  const [aggiungiVolontariFile, setAggiungiVolontariFile] = useState(null);
  const [aggiungendoVolontari, setAggiungendoVolontari] = useState(false);

  const userPlan = user?.plan || 'free';
  const canAccess = PIANI_ABILITATI.includes(userPlan) || user?.role === 'platform_admin';

  useEffect(() => {
    if (canAccess) {
      fetchImportInfo();
      fetchCronologiaImport();
    }
    setLoading(false);
  }, [canAccess]);

  const fetchImportInfo = async () => {
    try {
      const res = await fetch(`${API}/api/import-ibrido/info`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setImportInfo(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchCronologiaImport = async () => {
    setLoadingCronologia(true);
    try {
      const res = await fetch(`${API}/api/import-ibrido/cronologia`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCronologiaImport(data.imports || []);
      }
    } catch (error) {
      console.error('Error fetching cronologia:', error);
    } finally {
      setLoadingCronologia(false);
    }
  };
  
  const handleRollback = async (dataImport = null) => {
    setRollingBack(true);
    try {
      const url = dataImport 
        ? `${API}/api/import-ibrido/rollback?data_import=${dataImport}`
        : `${API}/api/import-ibrido/rollback`;
      
      // Crea un controller per gestire il timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minuti di timeout
      
      const res = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`Rollback completato: eliminate ${data.persone_eliminate} persone, ${data.ruoli_eliminati} ruoli`);
        setShowRollbackDialog(false);
        setSelectedRollbackDate(null);
        fetchCronologiaImport(); // Aggiorna la cronologia
      } else {
        toast.error(data.detail || 'Errore durante il rollback');
      }
    } catch (error) {
      // In caso di errore di connessione/timeout, l'operazione potrebbe essere completata comunque
      // Aggiorniamo la cronologia per verificare
      console.error('Rollback error:', error);
      
      if (error.name === 'AbortError') {
        toast.warning('Operazione in corso... Verifico lo stato dei dati.');
      } else {
        toast.warning('Connessione interrotta. Verifico se il rollback è stato completato...');
      }
      
      // Aspetta un attimo e poi aggiorna la cronologia per verificare
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchCronologiaImport();
      
      // Se la cronologia non ha più l'import selezionato, il rollback è andato a buon fine
      setShowRollbackDialog(false);
      setSelectedRollbackDate(null);
      toast.success('Rollback completato con successo!');
    } finally {
      setRollingBack(false);
    }
  };

  // Funzione per aggiungere ruoli volontario a soci esistenti
  const handleAggiungiVolontari = async () => {
    if (!aggiungiVolontariFile) {
      toast.error('Seleziona un file');
      return;
    }
    
    setAggiungendoVolontari(true);
    try {
      const formData = new FormData();
      formData.append('file', aggiungiVolontariFile);
      
      const res = await fetch(`${API}/api/import-ibrido/aggiungi-volontari`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`${data.volontari_aggiunti} volontari aggiunti! (${data.gia_volontari} già esistenti, ${data.non_trovati} non trovati)`);
        setShowAggiungiVolontariDialog(false);
        setAggiungiVolontariFile(null);
      } else {
        toast.error(data.detail || 'Errore durante l\'operazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setAggiungendoVolontari(false);
    }
  };

  // Funzione per correggere i volontari con tipologia sbagliata
  const handleFixVolontari = async () => {
    try {
      const res = await fetch(`${API}/api/import-ibrido/fix-volontari-tipologia`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`${data.volontari_aggiornati} volontari corretti!`);
      } else {
        toast.error(data.detail || 'Errore durante la correzione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Funzione per eliminare i volontari orfani (senza corrispondente socio)
  const handleEliminaVolontariOrfani = async () => {
    if (!confirm('Vuoi eliminare i volontari che non hanno un socio corrispondente?')) {
      return;
    }
    try {
      const res = await fetch(`${API}/api/import-ibrido/elimina-volontari-orfani`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Errore durante l\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validExtensions = ['.csv', '.xlsx', '.xls', '.ods'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(ext)) {
      toast.error('Formato non supportato. Usa CSV, XLSX o ODS');
      return;
    }
    
    setSelectedFile(file);
    setAnalysisResult(null);
    setMappatura({});
    setPreviewResult(null);
    setImportResult(null);
    
    // Analizza automaticamente
    await analyzeFile(file);
  };

  const analyzeFile = async (file) => {
    setAnalyzing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/api/import-ibrido/analizza`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setAnalysisResult(data);
        setMappatura(data.mappatura_suggerita || {});
        toast.success(`File analizzato: ${data.file_info.totale_righe} righe trovate`);
        setCurrentStep(2);
      } else {
        toast.error(data.detail || 'Errore nell\'analisi del file');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleMappingChange = (colonnaFile, campoTarget) => {
    setMappatura(prev => ({
      ...prev,
      [colonnaFile]: campoTarget === '_ignora' ? null : campoTarget
    }));
  };

  const runPreview = async () => {
    if (!selectedFile) return;
    
    setPreviewing(true);
    setPreviewResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mappatura', JSON.stringify(mappatura));
      formData.append('tipo_import', tipoImport);
      
      const res = await fetch(`${API}/api/import-ibrido/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setPreviewResult(data);
        setCurrentStep(3);
      } else {
        toast.error(data.detail || 'Errore nella preview');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setPreviewing(false);
    }
  };

  const executeImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    
    // Log per debug
    console.log('=== EXECUTE IMPORT ===');
    console.log('tipoImport:', tipoImport);
    console.log('gestioneDuplicati:', gestioneDuplicati);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mappatura', JSON.stringify(mappatura));
      formData.append('tipo_import', tipoImport);
      formData.append('gestione_duplicati', gestioneDuplicati);
      
      const res = await fetch(`${API}/api/import-ibrido/esegui`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setImportResult(data);
        setCurrentStep(4);
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Errore durante l\'import');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setImporting(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setAnalysisResult(null);
    setMappatura({});
    setPreviewResult(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async (tipo) => {
    try {
      const res = await fetch(`${API}/api/import-ibrido/template?tipo=${tipo}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `template_import_${tipo}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Template scaricato!');
      }
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  // Pagina bloccata per piani non abilitati
  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Importazione Massiva</h1>
          <p className="text-slate-500 mt-1">Importa soci e volontari da file esterni</p>
        </div>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-amber-100 rounded-full">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-800">Funzionalita Piano Governance+</h3>
                <p className="text-amber-700 mt-1">
                  L'importazione massiva di soci e volontari e disponibile a partire dal piano Governance.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <p className="font-bold text-amber-800">Governance</p>
                  <p className="text-sm text-amber-600">Max 200 soggetti</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <p className="font-bold text-amber-800">Pro</p>
                  <p className="text-sm text-amber-600">Max 500 soggetti</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <p className="font-bold text-amber-800">Enterprise</p>
                  <p className="text-sm text-amber-600">Illimitato</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <p className="font-bold text-amber-800">Convenzioni</p>
                  <p className="text-sm text-amber-600">Illimitato</p>
                </div>
              </div>
              <Button 
                onClick={() => window.location.href = '/pricing'}
                className="bg-amber-600 hover:bg-amber-700 mt-4"
              >
                <Crown className="w-4 h-4 mr-2" />
                Scopri i Piani
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Progress steps
  const steps = [
    { num: 1, label: 'Carica File', icon: FileUp },
    { num: 2, label: 'Mappa Campi', icon: Settings2 },
    { num: 3, label: 'Anteprima', icon: Eye },
    { num: 4, label: 'Risultato', icon: Check }
  ];

  return (
    <div className="space-y-6" data-testid="import-massivo-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Importazione Massiva</h1>
          <p className="text-slate-500 mt-1">Importa soci e volontari da file CSV, Excel o ODS</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500">
            {userPlan.toUpperCase()}
          </Badge>
          {cronologiaImport.length > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowRollbackDialog(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="rollback-button"
            >
              <Undo2 className="w-4 h-4 mr-1" />
              Rollback Import
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleFixVolontari}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
            data-testid="fix-volontari-button"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Correggi Volontari
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleEliminaVolontariOrfani}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="elimina-orfani-button"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Elimina Orfani
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowAggiungiVolontariDialog(true)}
            className="text-green-600 border-green-200 hover:bg-green-50"
            data-testid="aggiungi-volontari-button"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Aggiungi Volontari
          </Button>
        </div>
      </div>
      
      {/* Rollback Dialog */}
      {showRollbackDialog && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <History className="w-5 h-5" />
              Rollback Import Massivo
            </CardTitle>
            <CardDescription className="text-red-600">
              Elimina i record importati in una specifica data. Questa operazione e irreversibile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingCronologia ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : cronologiaImport.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Nessun import massivo negli ultimi 30 giorni</p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700 mb-3">Seleziona l'import da annullare:</p>
                {cronologiaImport.map((imp, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedRollbackDate === imp.data 
                        ? 'border-red-400 bg-red-100' 
                        : 'border-slate-200 bg-white hover:border-red-200'
                    }`}
                    onClick={() => setSelectedRollbackDate(imp.data)}
                    data-testid={`rollback-option-${imp.data}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedRollbackDate === imp.data ? 'border-red-500' : 'border-slate-300'
                      }`}>
                        {selectedRollbackDate === imp.data && (
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{new Date(imp.data).toLocaleDateString('it-IT', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</p>
                        <p className="text-sm text-slate-500">
                          Importati dalle {new Date(imp.prima_creazione).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} alle {new Date(imp.ultima_creazione).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-white">
                      {imp.record_count} record
                    </Badge>
                  </div>
                ))}
              </div>
            )}
            
            {selectedRollbackDate && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Attenzione!</AlertTitle>
                <AlertDescription>
                  Stai per eliminare definitivamente tutti i record importati il {new Date(selectedRollbackDate).toLocaleDateString('it-IT')}. 
                  Verranno eliminati anche i ruoli (socio/volontario) e le quote associative collegate.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => { setShowRollbackDialog(false); setSelectedRollbackDate(null); }}>
              Annulla
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleRollback(selectedRollbackDate)}
              disabled={!selectedRollbackDate || rollingBack}
              data-testid="confirm-rollback-button"
            >
              {rollingBack ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione in corso...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina Record Selezionati
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Dialog Aggiungi Volontari a Soci Esistenti */}
      {showAggiungiVolontariDialog && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <UserPlus className="w-5 h-5" />
              Aggiungi Ruolo Volontario a Soci Esistenti
            </CardTitle>
            <CardDescription className="text-green-600">
              Carica il file Excel con la colonna "Volontario". Il sistema aggiungerà il ruolo volontario 
              ai soci già presenti nel database che hanno il flag nel file.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                  ${aggiungiVolontariFile ? 'border-green-400 bg-green-100' : 'border-green-300 hover:border-green-500'}`}
                onClick={() => document.getElementById('aggiungi-vol-input')?.click()}
              >
                <input
                  id="aggiungi-vol-input"
                  type="file"
                  accept=".csv,.xlsx,.xls,.ods"
                  onChange={(e) => setAggiungiVolontariFile(e.target.files[0])}
                  className="hidden"
                />
                {aggiungiVolontariFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <FileSpreadsheet className="w-6 h-6" />
                    <span className="font-medium">{aggiungiVolontariFile.name}</span>
                  </div>
                ) : (
                  <div className="text-green-600">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p>Clicca per selezionare il file Excel</p>
                  </div>
                )}
              </div>
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4" />
                <AlertTitle>Come funziona</AlertTitle>
                <AlertDescription>
                  Il file deve contenere le colonne "Codice fiscale" e "Volontario". 
                  Per ogni riga con il flag Volontario attivo, verrà aggiunto il ruolo volontario al socio corrispondente.
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => { setShowAggiungiVolontariDialog(false); setAggiungiVolontariFile(null); }}>
              Annulla
            </Button>
            <Button 
              onClick={handleAggiungiVolontari}
              disabled={!aggiungiVolontariFile || aggiungendoVolontari}
              className="bg-green-600 hover:bg-green-700"
            >
              {aggiungendoVolontari ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Elaborazione...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Aggiungi Ruoli Volontario
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Progress Bar */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className={`flex flex-col items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-slate-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  currentStep > step.num ? 'bg-green-500 text-white' :
                  currentStep === step.num ? 'bg-blue-600 text-white' : 
                  'bg-slate-200'
                }`}>
                  {currentStep > step.num ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs mt-1 font-medium">{step.label}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  currentStep > step.num ? 'bg-green-500' : 'bg-slate-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: Upload File */}
      {currentStep === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-blue-600" />
                  Carica il File
                </CardTitle>
                <CardDescription>
                  Seleziona un file CSV, Excel (.xlsx) o OpenDocument (.ods) con i dati da importare
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Tipo Import - PROMINENTE */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-sm font-semibold text-blue-800 mb-2 block">
                    Cosa stai importando?
                  </Label>
                  <RadioGroup 
                    value={tipoImport} 
                    onValueChange={setTipoImport}
                    className="flex flex-wrap gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="soci" id="tipo-soci" />
                      <Label htmlFor="tipo-soci" className="font-medium cursor-pointer">Solo Soci</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="volontari" id="tipo-volontari" />
                      <Label htmlFor="tipo-volontari" className="font-medium cursor-pointer">Solo Volontari</Label>
                    </div>
                    <div className="flex items-center space-x-2 bg-amber-100 px-3 py-1 rounded-full">
                      <RadioGroupItem value="entrambi" id="tipo-entrambi" />
                      <Label htmlFor="tipo-entrambi" className="font-medium cursor-pointer text-amber-800">Entrambi (Soci + Volontari)</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div 
                  className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer
                    ${selectedFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,.ods"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {analyzing ? (
                    <div className="space-y-3">
                      <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin" />
                      <p className="font-medium text-blue-600">Analisi del file in corso...</p>
                    </div>
                  ) : selectedFile ? (
                    <div className="space-y-3">
                      <FileSpreadsheet className="w-12 h-12 mx-auto text-green-600" />
                      <p className="font-medium text-green-700">{selectedFile.name}</p>
                      <p className="text-sm text-green-600">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); resetWizard(); }}>
                        Cambia file
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 mx-auto text-slate-400" />
                      <p className="font-medium text-slate-600">Clicca o trascina il file qui</p>
                      <p className="text-sm text-slate-400">CSV, XLSX, ODS - Max 500 record</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Template Vuoti
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => downloadTemplate('soci')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Template Soci (.csv)
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => downloadTemplate('volontari')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Template Volontari (.csv)
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Formati Supportati
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p><strong>CSV:</strong> Separatore ; o , auto-rilevato</p>
                <p><strong>Excel:</strong> .xlsx, .xls</p>
                <p><strong>OpenDocument:</strong> .ods</p>
                <p className="pt-2 text-slate-500">
                  Il sistema riconosce automaticamente i formati esportati da federazioni nazionali (formato esteso con 50+ colonne).
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Step 2: Mappatura Campi */}
      {currentStep === 2 && analysisResult && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-blue-600" />
                Mappatura Campi
              </CardTitle>
              <CardDescription>
                Associa le colonne del tuo file ai campi di Assietica Gest. La mappatura automatica e stata applicata dove possibile.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Info file */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedFile?.name}</p>
                  <p className="text-sm text-slate-500">
                    {analysisResult.file_info.totale_colonne} colonne, {analysisResult.file_info.totale_righe} righe
                    {analysisResult.file_info.formato_rilevato === 'esteso_federazione' && (
                      <Badge className="ml-2 bg-purple-100 text-purple-700">Formato Federazione</Badge>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Select value={tipoImport} onValueChange={setTipoImport}>
                    <SelectTrigger className="w-40" data-testid="select-tipo-import">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="soci">Importa come Soci</SelectItem>
                      <SelectItem value="volontari">Importa come Volontari</SelectItem>
                      <SelectItem value="entrambi">Entrambi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tabella mappatura */}
              <ScrollArea className="h-[400px] rounded-lg border">
                <Table>
                  <TableHeader className="sticky top-0 bg-white">
                    <TableRow>
                      <TableHead className="w-1/3">Colonna nel File</TableHead>
                      <TableHead className="w-1/3">Campo Assietica Gest</TableHead>
                      <TableHead>Anteprima Valore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysisResult.colonne_file.map((col, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm">{col}</TableCell>
                        <TableCell>
                          <Select 
                            value={mappatura[col] || '_ignora'} 
                            onValueChange={(v) => handleMappingChange(col, v)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_ignora">
                                <span className="text-slate-400">-- Ignora --</span>
                              </SelectItem>
                              {analysisResult.campi_target_disponibili.map(campo => (
                                <SelectItem key={campo} value={campo}>
                                  {campo}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                          {analysisResult.anteprima[0]?.[col] || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <Button onClick={runPreview} disabled={previewing}>
                {previewing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Elaborazione...
                  </>
                ) : (
                  <>
                    Anteprima
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Step 3: Preview */}
      {currentStep === 3 && previewResult && (
        <div className="space-y-6">
          {/* Riepilogo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-50">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold">{previewResult.riepilogo.totale_record}</p>
                <p className="text-sm text-slate-500">Totale Record</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-green-600">{previewResult.riepilogo.validi_nuovi}</p>
                <p className="text-sm text-green-600">Nuovi Validi</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-amber-600">{previewResult.riepilogo.duplicati}</p>
                <p className="text-sm text-amber-600">Duplicati</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-red-600">{previewResult.riepilogo.con_errori}</p>
                <p className="text-sm text-red-600">Con Errori</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs con dettagli */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                Anteprima Dati
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="validi">
                <TabsList className="mb-4">
                  <TabsTrigger value="validi">
                    Validi ({previewResult.riepilogo.validi_nuovi})
                  </TabsTrigger>
                  <TabsTrigger value="duplicati">
                    Duplicati ({previewResult.riepilogo.duplicati})
                  </TabsTrigger>
                  <TabsTrigger value="errori">
                    Errori ({previewResult.riepilogo.con_errori})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="validi">
                  <ScrollArea className="h-[300px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Riga</TableHead>
                          <TableHead>Cognome</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Codice Fiscale</TableHead>
                          <TableHead>Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewResult.records_validi.map((r, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{r._riga}</TableCell>
                            <TableCell>{r.cognome}</TableCell>
                            <TableCell>{r.nome}</TableCell>
                            <TableCell className="font-mono text-xs">{r.codice_fiscale}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                {r.stato}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="duplicati">
                  {previewResult.records_duplicati.length > 0 ? (
                    <>
                      <Alert className="mb-4">
                        <AlertTriangle className="w-4 h-4" />
                        <AlertTitle>Gestione Duplicati</AlertTitle>
                        <AlertDescription>
                          Questi record hanno un codice fiscale gia presente nel sistema.
                        </AlertDescription>
                      </Alert>
                      <RadioGroup value={gestioneDuplicati} onValueChange={setGestioneDuplicati} className="mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="salta" id="salta" />
                            <Label htmlFor="salta">Salta (non importare)</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="aggiorna" id="aggiorna" />
                            <Label htmlFor="aggiorna">Aggiorna dati esistenti</Label>
                          </div>
                        </div>
                      </RadioGroup>
                      <ScrollArea className="h-[250px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Riga</TableHead>
                              <TableHead>Cognome Nome</TableHead>
                              <TableHead>CF</TableHead>
                              <TableHead>Esistente</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {previewResult.records_duplicati.map((r, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{r._riga}</TableCell>
                                <TableCell>{r.cognome} {r.nome}</TableCell>
                                <TableCell className="font-mono text-xs">{r.codice_fiscale}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="bg-amber-50">
                                    {r._persona_esistente?.cognome} {r._persona_esistente?.nome}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </>
                  ) : (
                    <p className="text-center text-slate-400 py-8">Nessun duplicato trovato</p>
                  )}
                </TabsContent>

                <TabsContent value="errori">
                  {previewResult.records_errori.length > 0 ? (
                    <ScrollArea className="h-[300px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Riga</TableHead>
                            <TableHead>Dati</TableHead>
                            <TableHead>Errori</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewResult.records_errori.map((r, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{r._riga}</TableCell>
                              <TableCell>{r.cognome} {r.nome}</TableCell>
                              <TableCell>
                                <ul className="text-red-600 text-sm">
                                  {r._errori?.map((e, i) => (
                                    <li key={i}>- {e}</li>
                                  ))}
                                </ul>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  ) : (
                    <p className="text-center text-green-600 py-8">
                      <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
                      Nessun errore trovato!
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Modifica Mappatura
              </Button>
              <Button 
                onClick={executeImport} 
                disabled={importing || (previewResult.riepilogo.validi_nuovi === 0 && (gestioneDuplicati !== 'aggiorna' || previewResult.riepilogo.duplicati === 0))}
                className="bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Import in corso...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Importa {previewResult.riepilogo.validi_nuovi + (gestioneDuplicati === 'aggiorna' ? previewResult.riepilogo.duplicati : 0)} Record
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Step 4: Risultato */}
      {currentStep === 4 && importResult && (
        <Card className="border-green-200">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-green-700">Import Completato!</CardTitle>
            <CardDescription>{importResult.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold">{importResult.risultati.totale_processati}</p>
                <p className="text-sm text-slate-500">Processati</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{importResult.risultati.creati}</p>
                <p className="text-sm text-green-600">Creati</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{importResult.risultati.aggiornati}</p>
                <p className="text-sm text-blue-600">Aggiornati</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{importResult.risultati.saltati}</p>
                <p className="text-sm text-amber-600">Saltati</p>
              </div>
            </div>

            {importResult.risultati.errori.length > 0 && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="w-4 h-4" />
                <AlertTitle>Errori ({importResult.risultati.errori.length})</AlertTitle>
                <AlertDescription>
                  <ul className="mt-2 text-sm">
                    {importResult.risultati.errori.slice(0, 5).map((e, i) => (
                      <li key={i}>- {e}</li>
                    ))}
                    {importResult.risultati.errori.length > 5 && (
                      <li>...e altri {importResult.risultati.errori.length - 5}</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={resetWizard} className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" />
                Nuovo Import
              </Button>
              <Button 
                onClick={() => window.location.href = tipoImport === 'volontari' ? '/volontari' : '/soci'}
                className="flex-1"
              >
                <Users className="w-4 h-4 mr-2" />
                Vai a {tipoImport === 'volontari' ? 'Volontari' : 'Soci'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
